import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Modal, ModalActions } from "../../../../../Components/Modal";
import ModalTitle from "../../../../../Components/Modal/ModalTitle";
import ModalBody from "../../../../../Components/Modal/ModalBody";
import { useTranslation } from "../../../../../context/Internationalization";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import GavelIcon from "@mui/icons-material/Gavel";
import ReportIcon from "@mui/icons-material/ReportGmailerrorred";
import GroupIcon from "@mui/icons-material/Group";

const StatusChip = ({ status, t }) => {
  const map = {
    pending: {
      icon: <ScheduleIcon fontSize="small" />,
      color: "warning",
      label: t("pending"),
    },
    accepted: {
      icon: <DoneAllIcon fontSize="small" />,
      color: "success",
      label: t("accepted"),
    },
    rejected: {
      icon: <CloseIcon fontSize="small" />,
      color: "error",
      label: t("rejected"),
    },
  };
  const cfg = map[status] || map.pending;
  return (
    <Chip size="small" icon={cfg.icon} color={cfg.color} label={cfg.label} />
  );
};

const ReasonChip = ({ reason }) => {
  const colorMap = {
    FAKE_PROFILE: "error",
    WRONG_TEAM: "warning",
    FALSE_STATS: "default",
  };
  return (
    <Chip
      size="small"
      icon={<ReportIcon fontSize="small" />}
      label={reason}
      color={colorMap[reason] || "default"}
    />
  );
};

const UserRow = ({ title, user, fallbackId }) => {
  const fullName = useMemo(() => {
    if (!user) return fallbackId || "-";
    const fn = (user.firstName || "").trim();
    const ln = (user.lastName || "").trim();
    const name = [fn, ln].filter(Boolean).join(" ");
    return name || fallbackId || "-";
  }, [user, fallbackId]);

  return (
    <Stack spacing={0.5}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ maxWidth: "220px" }}
      >
        {title}
      </Typography>
      <Typography variant="subtitle1">{fullName}</Typography>
      {user?._id && (
        <Typography variant="caption" color="text.secondary">
          ID: {user._id}
        </Typography>
      )}
    </Stack>
  );
};

const Section = ({ children }) => (
  <Box sx={{ display: "grid", gap: 1.25 }}>{children}</Box>
);

const Row = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gap: 1,
      gridTemplateColumns: "1fr auto",
      alignItems: "center",
    }}
  >
    {children}
  </Box>
);

const Label = ({ children }) => (
  <Typography variant="subtitle2" color="text.secondary">
    {children}
  </Typography>
);

const BanReportReviewModal = ({
  open,
  onClose,
  onSubmit,
  onUnban,
  report,
  loading = false,
  unbanning = false,
}) => {
  const { t } = useTranslation();
  const isPending = report?.status === "pending";

  const [action, setAction] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    setAction("accept");
    setNotes("");
  }, [open]);

  const handleSave = () => onSubmit({ action, decisionNotes: notes });
  const canUnban = Boolean(onUnban) && Boolean(report?.reportedUser?.isBanned);

  return (
    <Modal open={open} onClose={onClose} type={"info"}>
      <ModalTitle title={t("review_ban_report")} />

      <ModalBody>
        <Stack spacing={2.5}>
          {/* Header row */}
          <Row>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <GavelIcon color="info" />
              <Typography variant="subtitle1" fontWeight={700}>
                {t("review_ban_report")}
              </Typography>
            </Stack>
            <StatusChip status={report?.status} t={t} />
          </Row>

          <Typography variant="caption" color="text.secondary">
            {t("report_id")}: {report?._id}
          </Typography>

          <Divider />

          {/* Parties */}
          <Section>
            <UserRow
              title={t("reported_user")}
              user={report?.reportedUser}
              fallbackId={report?.reportedUserId}
            />
            <UserRow
              title={t("reporter_user")}
              user={report?.reporterUser}
              fallbackId={report?.reporterUserId}
            />
          </Section>

          <Divider />

          {/* Reason (+ team when WRONG_TEAM) */}
          <Section>
            <Label>{t("reason")}</Label>
            <ReasonChip reason={report?.reason} />
            {report?.reason === "WRONG_TEAM" && report?.reportedTeam?.name && (
              <Stack direction="row" spacing={1} alignItems="center">
                <GroupIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {t("team")}: {report.reportedTeam?.name}
                </Typography>
              </Stack>
            )}
          </Section>

          {/* Decision area */}
          {isPending ? (
            <>
              <Divider />
              <Section>
                <FormControl size="small" fullWidth>
                  <InputLabel id="action-select-label">
                    {t("review")}
                  </InputLabel>
                  <Select
                    labelId="action-select-label"
                    label={t("review")}
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="accept">{t("accept")}</MenuItem>
                    <MenuItem value="reject">{t("reject")}</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label={t("decision_notes")}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                />
              </Section>
            </>
          ) : (
            <>
              <Divider />
              <Section>
                {report?.decisionNotes ? (
                  <>
                    <Label>{t("decision_notes")}</Label>
                    <Box
                      sx={{
                        p: 1.25,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        minHeight: 64,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <Typography variant="body2">
                        {report.decisionNotes}
                      </Typography>
                    </Box>
                  </>
                ) : null}

                {canUnban && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => onUnban?.(report.reportedUserId)}
                    disabled={loading || unbanning}
                    sx={{ mt: 0.5 }}
                  >
                    {t("unban_user")}
                  </Button>
                )}
              </Section>
            </>
          )}
        </Stack>
      </ModalBody>

      <ModalActions>
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={onClose}
          disabled={loading || unbanning}
        >
          {t("close")}
        </Button>

        {isPending ? (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
          >
            {t("save")}
          </Button>
        ) : null}
      </ModalActions>
    </Modal>
  );
};

export default BanReportReviewModal;
